import { clipSeed, sinceMoment, vary, styleInto, type Clip, type CropKf } from "@hudhud/core";
import { FPS, HEIGHT, WIDTH, frameToMs, msToFrame } from "../types.js";
import { cutFlash, cutGlitch, cutShake, flickerAt, transitionFrames } from "./transitions.js";

export const SRC_W = 1920, SRC_H = 1080;
/** Height-fit width of a 16:9 frame inside the 9:16 canvas */
export const FIT_W = (HEIGHT * SRC_W) / SRC_H;

/** One step of a colour look, in the order the look applies them (contrast before saturate is not the same as after). */
export type LookFn = "contrast" | "saturate" | "sepia" | "brightness" | "hue-rotate" | "grayscale";
export interface LookStep { fn: LookFn; v: number }

/** The colour look as ordered steps; strength 0..1 blends toward natural. Empty = natural. */
export function lookSteps(preset: string, strength: number): LookStep[] {
  const k = Math.max(0, Math.min(1, strength));
  const mix = (a: number, b: number) => 1 + (b - 1) * k; // 1 = neutral
  switch (preset) {
    case "warm": return [{ fn: "contrast", v: mix(1, 1.06) }, { fn: "saturate", v: mix(1, 1.12) }, { fn: "sepia", v: 0.12 * k }, { fn: "brightness", v: mix(1, 1.03) }];
    case "golden": return [{ fn: "contrast", v: mix(1, 1.1) }, { fn: "saturate", v: mix(1, 1.2) }, { fn: "sepia", v: 0.28 * k }, { fn: "brightness", v: mix(1, 1.04) }, { fn: "hue-rotate", v: -4 * k }];
    case "cool": return [{ fn: "contrast", v: mix(1, 1.05) }, { fn: "saturate", v: mix(1, 0.95) }, { fn: "hue-rotate", v: -6 * k }, { fn: "brightness", v: mix(1, 1.02) }];
    case "cinematic": return [{ fn: "contrast", v: mix(1, 1.18) }, { fn: "saturate", v: mix(1, 0.85) }, { fn: "sepia", v: 0.08 * k }, { fn: "brightness", v: mix(1, 0.98) }];
    case "soft": return [{ fn: "contrast", v: mix(1, 0.94) }, { fn: "saturate", v: mix(1, 0.92) }, { fn: "brightness", v: mix(1, 1.06) }];
    case "mono": return [{ fn: "grayscale", v: k }, { fn: "contrast", v: mix(1, 1.1) }];
    default: return [];
  }
}
const stepCss = (s: LookStep) => (s.fn === "hue-rotate" ? `hue-rotate(${s.v}deg)` : `${s.fn}(${s.v})`);
/** CSS filter for each colour look; strength 0..1 blends toward natural. */
export function lookFilter(preset: string, strength: number): string {
  const steps = lookSteps(preset, strength);
  return steps.length ? steps.map(stepCss).join(" ") : "none";
}
/** The grade with the flicker's brightness folded in, as steps (the flicker is rounded the way the CSS rounds it). */
export function gradeSteps(preset: string, strength: number, flicker = 1): LookStep[] {
  const steps = lookSteps(preset, strength);
  return flicker !== 1 ? [...steps, { fn: "brightness", v: Number(flicker.toFixed(3)) }] : steps;
}
/** The grade with the flicker's brightness folded in: "none" alone when there is nothing to draw, never "none brightness(…)". */
export function gradeFilter(preset: string, strength: number, flicker = 1): string {
  const grade = lookFilter(preset, strength);
  const parts = [grade === "none" ? "" : grade, flicker !== 1 ? `brightness(${flicker.toFixed(3)})` : ""].filter(Boolean);
  return parts.length ? parts.join(" ") : "none";
}

/** Punch-in cut: each piece starts slightly zoomed and settles over ~14 frames. `amount` varies per clip (4–8 %). */
export function punchZoom(localFrame: number, enabled: boolean, amount = 0.06): number {
  if (!enabled) return 1;
  const t = Math.min(1, localFrame / 14);
  const eased = 1 - Math.pow(1 - t, 3);
  return 1 + amount - amount * eased;
}

/** Slow drift: a small zoom spread across the piece, alternating direction per piece; the amount varies per clip (3–5 %). */
export function driftZoom(localFrame: number, pieceFrames: number, pieceIndex: number, enabled: boolean, amount = 0.04): number {
  if (!enabled || pieceFrames <= 1) return 1;
  const t = Math.min(1, localFrame / pieceFrames);
  return pieceIndex % 2 === 0 ? 1 + amount * t : 1 + amount - amount * t;
}

/** A directed slow push: zoom grows over a window (a story building, a line that deserves a lean-in). */
export function pushZoom(clip: Pick<Clip, "direction">, sourceMs: number): number {
  let best = 1;
  for (const m of clip.direction.moments) {
    if (m.kind !== "push") continue;
    const a = m.atMs, b = m.endMs ?? m.atMs + 5000;
    if (sourceMs < a || sourceMs > b + 600) continue;
    const t = Math.min(1, (sourceMs - a) / Math.max(1, b - a));
    const eased = 1 - Math.pow(1 - t, 2);
    best = Math.max(best, 1 + 0.08 * m.intensity * eased);
  }
  return best;
}

export function cropAt(keyframes: CropKf[], ms: number): CropKf {
  if (!keyframes.length) return { ms, cx: 0.5, cy: 0.4, zoom: 1 };
  if (ms <= keyframes[0].ms) return keyframes[0];
  const last = keyframes[keyframes.length - 1];
  if (ms >= last.ms) return last;
  const i = keyframes.findIndex((k) => k.ms > ms);
  const a = keyframes[i - 1], b = keyframes[i];
  const t = (ms - a.ms) / Math.max(1, b.ms - a.ms);
  const lerp = (x: number, y: number) => x + (y - x) * t;
  return { ms, cx: lerp(a.cx, b.cx), cy: lerp(a.cy, b.cy), zoom: lerp(a.zoom, b.zoom) };
}

export interface CropBox { width: number; height: number; left: number; top: number }
/** Placement of the height-fitted source so that (cx, cy) sits at the canvas centre with the given zoom, clamped inside the frame. */
export function cropBox(k: CropKf): CropBox {
  const w = FIT_W * k.zoom, h = HEIGHT * k.zoom;
  let left = WIDTH / 2 - k.cx * w;
  let top = HEIGHT / 2 - k.cy * h;
  left = Math.min(0, Math.max(WIDTH - w, left));
  top = Math.min(0, Math.max(HEIGHT - h, top));
  return { width: w, height: h, left, top };
}
/** The same as CSS. */
export function cropStyle(k: CropKf): { position: "absolute" } & CropBox {
  return { position: "absolute", ...cropBox(k) };
}

export const voiceGain = (clip: Pick<Clip, "voiceMuted" | "voiceDb">) => (clip.voiceMuted ? 0 : Math.min(2, Math.pow(10, clip.voiceDb / 20)));

/** The film grain tile: fractal noise, overlay-blended at 0.35 × amount. */
export const GRAIN_URI = "data:image/svg+xml;utf8," + encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.5 0 0 0 0 0.5 0 0 0 0 0.5 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(#n)'/></svg>`);
export const GRAIN_TILE = 220;
export const grainOpacity = (amount: number) => 0.35 * amount;
/** Letterbox bars: 7 % of the height top and bottom. */
export const BARS_FRACTION = 0.07;
/** The vignette's edge darkness for a clip setting. */
export const vignetteAlpha = (vignette: number) => 0.75 * vignette;

/** The treated room behind a cut-out speaker, as numbers (the CSS rounds the same way). */
export function roomLook(bg: Clip["background"]): { blur?: number; brightness?: number; saturate?: number } {
  if (bg.mode === "blur") return { blur: Math.round(8 + 28 * bg.amount), brightness: Number((1 - 0.35 * bg.amount).toFixed(2)) };
  if (bg.mode === "dark") return { brightness: Number((1 - 0.85 * bg.amount).toFixed(2)), saturate: 0.7 };
  return {};
}

/** Everything one video piece needs at one of its own frames: the crop with every zoom folded in, and the cut punctuation. */
export interface PieceFrame {
  sourceMs: number;
  crop: CropKf;
  shake: { x: number; y: number; rot: number };
  flicker: number;
  glitch: number;
  flash: number;
}
export function pieceFrame(clip: Clip, segIndex: number, frame: number): PieceFrame {
  const seg = clip.segments[segIndex];
  const sourceMs = seg.inMs + frameToMs(frame);
  const k0 = cropAt(clip.crop, sourceMs);
  const pieceFrames = Math.max(1, msToFrame(seg.outMs - seg.inMs));
  const seed = clipSeed(clip);
  const zoom = k0.zoom * (clip.frameZoom ?? 1) * punchZoom(frame, styleInto(clip, segIndex) === "punch", 0.04 + 0.04 * vary(seed, 1)) * driftZoom(frame, pieceFrames, segIndex, clip.effects.drift, 0.03 + 0.02 * vary(seed, 2)) * pushZoom(clip, sourceMs);
  const cutFrame = segIndex === 0 ? -1 : frame; // clip-wide effects punctuate every cut after the first piece…
  const at = (kind: "shake" | "flash" | "glitch") => { const m = sinceMoment(clip, kind, sourceMs); return m.sinceMs < 0 ? { f: -1, i: 0 } : { f: Math.round(m.sinceMs / (1000 / FPS)), i: m.intensity }; };
  const dShake = at("shake"), dFlash = at("flash"), dGlitch = at("glitch"); // …directed ones land exactly where the speech asks
  const s1 = cutShake(cutFrame, clip.effects.shake), s2 = cutShake(dShake.f, dShake.f >= 0);
  const shake = { x: s1.x + s2.x * dShake.i, y: s1.y + s2.y * dShake.i, rot: s1.rot + s2.rot * dShake.i };
  const flicker = flickerAt(frame + segIndex * 97, clip.effects.flicker);
  const glitch = Math.max(cutGlitch(cutFrame, clip.effects.glitch), cutGlitch(dGlitch.f, dGlitch.f >= 0) * dGlitch.i);
  const flash = Math.max(cutFlash(cutFrame, clip.effects.flash), cutFlash(dFlash.f, dFlash.f >= 0) * dFlash.i);
  return { sourceMs, crop: { ...k0, zoom }, shake, flicker, glitch, flash };
}

/** Frames each piece occupies on the timeline: [from, duration]; with a transition the piece keeps playing `overlap` frames
 *  past its out-point (the source has more footage there) so the next piece can animate in over it. Total length is unchanged. */
export function pieceSpans(clip: Clip): { from: number; dur: number; overlap: number }[] {
  let from = 0;
  return clip.segments.map((seg, i) => {
    const dur = Math.max(1, msToFrame(seg.outMs - seg.inMs));
    const isLast = i === clip.segments.length - 1;
    const overlap = isLast ? 0 : transitionFrames(clip, i); // the cut after this piece, with its own style
    const span = { from, dur: dur + overlap, overlap };
    from += dur;
    return span;
  });
}

/** Frames the voice crossfades over at a join: the outgoing piece runs on this far, fading, while the incoming fades in from the cut. */
export const VOICE_XFADE_FRAMES = 3;
/** The crossfade at the join after piece i, shorter when either piece is very short (never more than half of one). */
export function voiceXfadeFrames(clip: Pick<Clip, "segments">, i: number): number {
  const a = clip.segments[i], b = clip.segments[i + 1];
  if (!a || !b) return 0;
  return Math.max(0, Math.min(VOICE_XFADE_FRAMES, Math.floor(msToFrame(a.outMs - a.inMs) / 2), Math.floor(msToFrame(b.outMs - b.inMs) / 2)));
}
/**
 * The voice's level `f` frames into piece i's audio sequence (its own length plus the crossfade tail): eased in over the join
 * before it, held, eased out through the join after it. Two neighbours' ramps add up to one, so a join never dips or doubles.
 */
export function voiceJoinGain(clip: Pick<Clip, "segments">, i: number, f: number): number {
  const own = Math.max(1, msToFrame(clip.segments[i].outMs - clip.segments[i].inMs));
  const lead = i === 0 ? 0 : voiceXfadeFrames(clip, i - 1), tail = voiceXfadeFrames(clip, i);
  if (f < 0 || f >= own + tail) return 0;
  const fadeIn = lead === 0 ? 1 : Math.min(1, (f + 1) / (lead + 1));
  const fadeOut = f < own ? 1 : Math.max(0, 1 - (f - own + 1) / (tail + 1));
  return fadeIn * fadeOut;
}
